/**
 * The parts list as a person edits it: one row per *kind* of part, not one per
 * bill-of-materials line.
 *
 * The BOM stores seven joints as seven lines (§2.6, `checkBomLine`: a
 * serialized line has quantity 1 and its own position), which is right for the
 * tree and wrong for a form — nobody wants to add the same actuator seven
 * times. So the editor groups lines by child type into a row with a count, and
 * flattens them back on save. Nothing here decides *whether* a save edits the
 * revision or forks a new one; that is still `mayEditLines`, applied by
 * `saveStructure` and nowhere else.
 *
 * Every function clamps rather than refuses, because the caller is a stepper
 * or a search result somebody tapped.
 */

import { currentRevisionOf } from './structure'
import type { DesignLineRef, DesignRevisionRef, DesignSource, DesignTypeRef } from './structure'
import { positionFor, positionStem } from './authoring'
import { parsePositionName } from './positions'

/** What a type form sends: one BOM line, before the server resolves revisions. */
export interface AuthoredLine {
  readonly childTypeId: string
  readonly quantity: number
  /** Present exactly when the child is serialized — a position is a slot. */
  readonly position: string | null
}

/**
 * One row of the parts list.
 *
 * `slots` for a serialized child — how many there are *is* how many positions
 * it has, so the count is never stored apart from the names. `count` for a
 * consumed one, which has a quantity in its unit and no position at all.
 */
export type PartsEntry =
  | { readonly kind: 'slots'; readonly childTypeId: string; readonly positions: readonly string[] }
  | { readonly kind: 'count'; readonly childTypeId: string; readonly quantity: number }

/** The most positions one row will grow to. A held-down stepper is not a design decision. */
export const MAX_SLOTS = 64

/**
 * Lines → rows, in the order each child type first appears.
 *
 * Two counted lines of the same type are summed into one row: the form has one
 * quantity per part, and a build consumes the sum either way.
 */
export function groupParts(lines: readonly AuthoredLine[]): readonly PartsEntry[] {
  const out: PartsEntry[] = []
  const at = new Map<string, number>()
  for (const line of lines) {
    const key = `${line.position === null ? 'c' : 's'}:${line.childTypeId}`
    const i = at.get(key)
    const prior = i === undefined ? undefined : out[i]
    if (prior === undefined || i === undefined) {
      at.set(key, out.length)
      out.push(
        line.position === null
          ? { kind: 'count', childTypeId: line.childTypeId, quantity: line.quantity }
          : { kind: 'slots', childTypeId: line.childTypeId, positions: [line.position] },
      )
    } else if (prior.kind === 'slots' && line.position !== null) {
      out[i] = { ...prior, positions: [...prior.positions, line.position] }
    } else if (prior.kind === 'count') {
      out[i] = { ...prior, quantity: prior.quantity + line.quantity }
    }
  }
  return out
}

/** Rows → lines, ready for `saveStructure`. Positions are trimmed; nothing else is touched. */
export function flattenParts(entries: readonly PartsEntry[]): readonly AuthoredLine[] {
  return entries.flatMap((e): AuthoredLine[] =>
    e.kind === 'slots'
      ? e.positions.map((p) => ({ childTypeId: e.childTypeId, quantity: 1, position: p.trim() }))
      : [{ childTypeId: e.childTypeId, quantity: e.quantity, position: null }],
  )
}

/**
 * Has the person actually changed what it is made of?
 *
 * Compared after grouping both sides, so opening a type whose saved lines are
 * interleaved (joint, screw, joint) and saving its *name* is not a change —
 * which matters, because on a frozen revision a change forks the next one.
 */
export function partsChanged(saved: readonly AuthoredLine[], entries: readonly PartsEntry[]): boolean {
  return JSON.stringify(flattenParts(groupParts(saved))) !== JSON.stringify(flattenParts(entries))
}

function clampCount(count: number, fallback: number): number {
  if (!Number.isFinite(count)) return fallback
  return Math.min(MAX_SLOTS, Math.max(0, Math.floor(count)))
}

/**
 * Names for `count` new slots of one part: `stem` alone for one, otherwise
 * `stem_1`, `stem_2`, … skipping any name already taken under the same parent.
 *
 * This is the "Name them" button — the person asked for the series, so every
 * name in the row is replaced.
 */
export function numberedPositions(stem: string, count: number, taken: readonly string[]): readonly string[] {
  const n = clampCount(count, 0)
  if (n === 0) return []
  const base = positionStem(stem)
  if (n === 1) return [positionFor(base, taken)]
  const used = new Set(taken)
  const out: string[] = []
  for (let k = 1; out.length < n; k += 1) {
    const candidate = `${base}_${k}`
    if (!used.has(candidate)) {
      out.push(candidate)
      used.add(candidate)
    }
  }
  return out
}

/**
 * The stepper: grow or shrink a row's positions to `count`.
 *
 * Shrinking drops from the end. Growing keeps every name already there and
 * adds the next free `stem_<n>` — except that a lone bare name nobody has
 * saved yet (a part just picked, named `joint`) becomes the start of a series,
 * so stepping to seven reads `joint_1 … joint_7` rather than `joint, joint_2 …`.
 * A *saved* name (`fixed`) is never renamed implicitly: telemetry is keyed by
 * it, and a rename should be something somebody chose.
 */
export function resizePositions(input: {
  readonly stem: string
  readonly current: readonly string[]
  readonly count: number
  /** Names used by the parent's other rows. */
  readonly taken: readonly string[]
  /** Names already saved on the revision. */
  readonly fixed?: readonly string[]
}): readonly string[] {
  const n = clampCount(input.count, input.current.length)
  if (n <= input.current.length) return input.current.slice(0, n)
  const base = positionStem(input.stem)
  const [only] = input.current
  if (input.current.length === 1 && only === base && !(input.fixed ?? []).includes(only)) {
    return numberedPositions(base, n, input.taken)
  }
  const used = new Set([...input.taken, ...input.current])
  const out = [...input.current]
  for (let k = 1; out.length < n; k += 1) {
    const candidate = `${base}_${k}`
    if (!used.has(candidate)) {
      out.push(candidate)
      used.add(candidate)
    }
  }
  return out
}

/**
 * What is wrong with each position name, for the message under its field.
 *
 * Null where it is fine. The grammar is `parsePositionName`'s — the same rule
 * the server applies — and "used twice" is `checkRevisionLines`'s, said per
 * field instead of once for the whole list.
 */
export function positionIssues(names: readonly string[]): readonly (string | null)[] {
  const counts = new Map<string, number>()
  for (const n of names) counts.set(n.trim(), (counts.get(n.trim()) ?? 0) + 1)
  return names.map((raw) => {
    const name = raw.trim()
    if (name === '') return 'Give this position a name'
    const parsed = parsePositionName(name)
    if (!parsed.ok) {
      return name.includes('.')
        ? 'One name, no dots — the path is worked out from the tree'
        : 'Lowercase letters, digits and _ only, starting with a letter'
    }
    if ((counts.get(name) ?? 0) > 1) return 'Used twice — each position needs its own name'
    return null
  })
}

/**
 * The design tree as it *would* be, for the preview beside the form.
 *
 * Swaps the draft's lines in for the type's current revision — or adds a
 * stand-in revision for a type that has none, or does not exist yet — and the
 * draft's name and shape in for the stored ones. Nothing is written; the result
 * goes straight to `designTree`.
 */
export function withDraftStructure(
  source: DesignSource,
  draft: { readonly type: DesignTypeRef; readonly lines: readonly DesignLineRef[] },
): { readonly source: DesignSource; readonly revisionId: string } {
  const id = draft.type.id
  const types = source.types.some((t) => t.id === id)
    ? source.types.map((t) => (t.id === id ? { ...t, ...draft.type } : t))
    : [...source.types, draft.type]
  const current = currentRevisionOf(source.revisions, id)
  if (current !== undefined) {
    return {
      source: {
        types,
        revisions: source.revisions.map((r) => (r.id === current.id ? { ...r, lines: draft.lines } : r)),
      },
      revisionId: current.id,
    }
  }
  const standIn: DesignRevisionRef = {
    id: `draft:${id}`,
    componentTypeId: id,
    name: 'draft',
    effectiveFrom: '9999-12-31',
    supersededById: null,
    lines: draft.lines,
  }
  return { source: { types, revisions: [...source.revisions, standIn] }, revisionId: standIn.id }
}
